/**
 * Pure helpers for `useBuilderSubject`.
 *
 * Kept in a separate module so the hook file stays focused on the fetch +
 * memoization it owns, and so `BuilderDraft` — whose only producer is the
 * hook — lives next to the code that shapes it.
 */

import type { PreviewOverlay } from '@/lib/crm/preview';
import type { ProposedAction } from '@/lib/proposals/core/types';
import { isTimelineEventPayload } from '@/lib/proposals/core/types';
import { projectTypedCreatePayload } from '@/lib/proposals/utils/projectTypedCreatePayload';

export interface BuilderDraft {
  /** The proposal carrying it, so callers can link to or approve it. */
  proposalId: number;
  /**
   * The subject entity's preview overlay — the same shape the action center
   * derives and the record surface projects. `entityTypeName` is the concrete
   * type approval will create (or the saved record's type on the edit path),
   * and `properties` are the proposed values keyed by CRM property name.
   */
  overlay: PreviewOverlay;
}

/**
 * The entity a builder proposal is about: the first entity, in execution
 * order, that any of its actions creates or edits.
 *
 * Every action in a builder proposal targets the same record, so "first" is
 * merely a tie-break — but it is a deterministic one, taken from the actions
 * themselves rather than from the iteration order of a derived map.
 */
export function subjectEntityIdOf(
  actions: readonly ProposedAction[] | undefined
): string | undefined {
  if (!actions || actions.length === 0) {
    return undefined;
  }
  const sorted = [...actions].sort(
    (a, b) => a.execution_order - b.execution_order
  );
  for (const action of sorted) {
    const typed = projectTypedCreatePayload(action);
    if (typed) {
      return typed.entityId;
    }
    if (isTimelineEventPayload(action.payload) && action.payload.entity_id) {
      return action.payload.entity_id;
    }
  }
  return undefined;
}
